import React from 'react';

export const DEFAULT_SEARCH_HIGHLIGHT_COLOR = '#ffff00';

export const normalizeSearchText = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

export type HighlightSearchInTextOptions = {
  highlightColor?: string;
  /** When search contains `%`, highlight matching numeric percentages in text */
  highlightPercentage?: boolean;
};

const findNormalizedTextMatch = (
  text: string,
  needle: string,
  normalize: (value: string) => string
): { start: number; end: number } | null => {
  if (!needle) return null;
  for (let start = 0; start < text.length; start++) {
    for (let end = start + 1; end <= text.length; end++) {
      if (normalize(text.slice(start, end)) === needle) {
        return { start, end };
      }
    }
  }
  return null;
};

const highlightPercentageMatches = (
  text: string,
  search: string,
  highlightColor: string
): React.ReactNode => {
  const searchPct = Number(search.replace(/%/g, '').trim());
  if (!Number.isFinite(searchPct)) return text;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  const pctRegex = /\d+(?:\.\d+)?%?/g;
  let match: RegExpExecArray | null;

  while ((match = pctRegex.exec(text)) !== null) {
    const raw = match[0];
    const num = Number(raw.replace(/%/g, ''));
    if (num !== searchPct) continue;
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <span key={key++} style={{ backgroundColor: highlightColor }}>
        {raw}
      </span>
    );
    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? <>{parts}</> : text;
};

const highlightSubstringMatches = (
  text: string,
  search: string,
  highlightColor: string,
  normalize: (value: string) => string
): React.ReactNode => {
  const needle = normalize(search);
  if (!needle) return text;

  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining) {
    const match = findNormalizedTextMatch(remaining, needle, normalize);
    if (!match) {
      parts.push(remaining);
      break;
    }
    if (match.start > 0) parts.push(remaining.slice(0, match.start));
    parts.push(
      <span key={key++} style={{ backgroundColor: highlightColor }}>
        {remaining.slice(match.start, match.end)}
      </span>
    );
    remaining = remaining.slice(match.end);
  }

  return <>{parts}</>;
};

/**
 * Highlights search matches inside display text for autocomplete/list dropdowns.
 * Matching is case-insensitive and ignores diacritics.
 */
export const highlightSearchInText = (
  text: string,
  search: string,
  options?: HighlightSearchInTextOptions
): React.ReactNode => {
  const input = search.trim();
  if (!input) return text;

  const highlightColor = options?.highlightColor ?? DEFAULT_SEARCH_HIGHLIGHT_COLOR;
  const shouldHighlightPercentage = options?.highlightPercentage ?? input.includes('%');

  if (shouldHighlightPercentage && input.includes('%')) {
    return highlightPercentageMatches(text, input, highlightColor);
  }

  return highlightSubstringMatches(text, input, highlightColor, normalizeSearchText);
};
